import { Checkbox, Switch, Typography } from 'antd';
import AccessManageDtvAgentMap from './AccessManageDtvAgentMap'
import { useAgentMapData, useAgentMapsPaging, useTitle } from 'hooks';
import { useEffect, useState, useCallback, useMemo } from 'react';
import MultipleAgentMap from './MultipleAgentMap';
import OnlineAgentMap from './OnlineAgentMap';
import Dashboard from './Dashboard';
import {
    useLazyCountChatSessionQuery,
    useLazyGetCallDetailsQuery,
    onReceiveUpdateChatStatDetailEvent,
    useLazyGetOnlineAgentMapSeatsQuery
} from 'services';
import { CountChatSessionResultDto, GetCallDetailsDto } from 'types';
import { sidebarMenuMessages } from 'messages';
import { PageHeader } from 'components/common';
import { useAgentMapStore } from 'hooks/agent-maps/useAgentMapStore';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { useAgentMapView } from '../../hooks/agent-maps/useAgentMapView';
import { setOnlineAgentMapData } from 'store/features/agent-map/agent-map.slice';

export type SingleAgentMapSingleStatusesType = {
    extension: string;
    agentName: string;
    state: string;
    backgroundColor: string;
    iconColor: string;
    textColor: string;
    startTime: number;
};

export type chartDataType = {
    extensionId: string;
    statusName: string
}

/**
 * Component chính quản lý dashboard agent map
 */
const AccessManageDtv = () => {
    useTitle(sidebarMenuMessages.dashboardAgentMap);
    const dispatch = useDispatch();

    const {
        selectMaps, // This is the action dispatcher from the hook
        mapData: multipleAgentMapsFromStore, // Renamed to avoid conflict
    } = useAgentMapStore();

    // State quản lý selected agent map
    const [getAgentMapDetail, setGetAgentMapDetail] = useState<number | number[]>(0);
    const { agentMaps } = useAgentMapsPaging({ isActive: true, pageSize: 50 });
    const [singleAgentMapAgentStatuses, setSingleAgentMapAgentStatuses] = useState<SingleAgentMapSingleStatusesType[]>([]);
    const [showCounters, setShowCounters] = useState<boolean>(false);

    // Initialize selectedMapKeys from localStorage or default to ["online"]
    const [selectedMapKeys, setSelectedMapKeys] = useState<string[]>(() => {
        try {
            const savedSelection = localStorage.getItem('agentMapSelection');
            if (savedSelection) {
                const parsed = JSON.parse(savedSelection);
                // Validate that it's an array of strings
                if (Array.isArray(parsed) && parsed.every(item => typeof item === 'string')) {
                    //console.log('🔄 Restored agent map selection from localStorage:', parsed);
                    return parsed;
                }
            }
        } catch (error) {
            //console.warn('⚠️ Failed to parse saved agent map selection:', error);
        }
        // Default to online if no valid saved selection
        //console.log('📍 Using default agent map selection: ["online"]');
        return ["online"];
    });

    // Custom hook quản lý view thay vì các state riêng lẻ
    const {
        onlineMapSelected,
        setOnlineMapSelected,
        onlyOnlineMapSelected,
        setOnlyOnlineMapSelected,
        renderMultipleAgentMap,
        setRenderMultipleAgentMap,
        handleMapSelectionChange
    } = useAgentMapView({
        setGetAgentMapDetail,
        selectMaps
    });

    // Track if we've loaded online data at least once
    const [onlineDataLoaded, setOnlineDataLoaded] = useState(false);

    // Use a lazy query so we can always trigger fetch again
    const [fetchOnlineSeats, { data: onlineSeatsApiResponse }] =
        useLazyGetOnlineAgentMapSeatsQuery();

    // onlineAgentSeats is from API call
    const onlineAgentSeatsFromAPI = onlineSeatsApiResponse?.data;

    // Dashboard data management - Ensure these are defined
    const [dashboardCallStats, setDashboardCallStats] = useState<GetCallDetailsDto[]>([]);
    const [dashboardChatStats, setDashboardChatStats] = useState<CountChatSessionResultDto | undefined>(undefined);
    const [getCallDetailsTrigger, { data: getCallDetails }] = useLazyGetCallDetailsQuery();
    const [getChatSessionDetailsTrigger, { data: getChatSessionDetails }] = useLazyCountChatSessionQuery();

    // Get dashboard data from Redux
    const reduxCallStats = useSelector((state: RootState) => state.agentMap.dashboard?.callStats || []);
    const lastUpdateTime = useSelector((state: RootState) => state.agentMap.lastSocketUpdateTime); // This is for the dashboard's key prop if re-added

    // Fetch call details
    useEffect(() => {
        getCallDetailsTrigger();
    }, [getCallDetailsTrigger]);

    // Handle chat session updates
    useEffect(() => {
        const dateRange = getTodayDateRange(); // Ensure getTodayDateRange is defined in this component or imported
        getChatSessionDetailsTrigger({
            ...dateRange,
            isHandleByAi: false, // Pass undefined to not filter by this field
        });

        onReceiveUpdateChatStatDetailEvent((data) => {

            getChatSessionDetailsTrigger({
                ...dateRange,
                isHandleByAi: false, // Pass undefined to not filter by this field
            });
        });

    }, [getChatSessionDetailsTrigger]); // Removed getTodayDateRange if it's stable or memoized

    // Update dashboard from Redux
    useEffect(() => {
        if (reduxCallStats && reduxCallStats.length > 0) {
            setDashboardCallStats([...reduxCallStats]);
        }
    }, [reduxCallStats, lastUpdateTime]);

    // Update dashboard from API
    useEffect(() => {
        if (getCallDetails?.data && getCallDetails.data.length > 0) {
            // Dispatch all API data to Redux
            getCallDetails.data.forEach(sourceData => {
                if (sourceData.callSource) {
                    const updates: Record<string, number> = {};

                    if (sourceData.incomingCalls !== undefined) updates.incomingCalls = sourceData.incomingCalls;
                    if (sourceData.waitingForAgent !== undefined) updates.waitingForAgent = sourceData.waitingForAgent;
                    if (sourceData.isTalking !== undefined) updates.isTalking = sourceData.isTalking;
                    if (sourceData.agentReceive !== undefined) updates.agentReceive = sourceData.agentReceive;
                    if (sourceData.botReceive !== undefined) updates.botReceive = sourceData.botReceive;
                    if (sourceData.missedCall !== undefined) updates.missedCall = sourceData.missedCall;
                    if (sourceData.totalCall !== undefined) updates.totalCall = sourceData.totalCall;

                    dispatch({
                        type: 'agentMap/batchUpdateCallStats',
                        payload: {
                            callSource: sourceData.callSource,
                            updates
                        }
                    });
                }
            });
            setDashboardCallStats(getCallDetails.data);
        }
    }, [getCallDetails, dispatch]);

    // Update chat stats
    useEffect(() => {
        if (getChatSessionDetails?.data) {
            setDashboardChatStats(getChatSessionDetails.data);
        }
    }, [getChatSessionDetails]);

    // Helper function (if not already present or imported)
    const getTodayDateRange = useCallback(() => {
        const today = new Date();
        const startOfDay = new Date(today.setHours(0, 0, 0, 0));
        const endOfDay = new Date(today.setHours(23, 59, 59, 999));
        return {
            fromDate: startOfDay.toISOString(),
            toDate: endOfDay.toISOString()
        };
    }, []);


    // Effect to load initial online data for the chart and for the online map display
    useEffect(() => {
        if (!onlineDataLoaded) {

            fetchOnlineSeats().then(response => {
                if (response.data?.data) {
                    // Dispatch to setOnlineAgentMapData which handles both mapData[-999] and onlineDataForChart
                    dispatch({
                        type: 'agentMap/setOnlineAgentMapData',
                        payload: response.data.data
                    });
                    setOnlineDataLoaded(true);
                }
            });
        }
    }, [dispatch, onlineDataLoaded, fetchOnlineSeats]);


    // Log when component mounts with restored selection
    useEffect(() => {
        console.log('🚀 AccessManageDtv mounted with selectedMapKeys:', selectedMapKeys);
    }, []); // Run only on mount

    // Auto-select "online" map in Redux store on initial load or when selection becomes empty.
    useEffect(() => {
        if (selectedMapKeys.length === 0) {
            // No maps are selected by the user
            selectMaps([], false, false); // Clear all maps from display, don't preserve online
            // Update local state for view rendering consistency
            setOnlineMapSelected(false);
            setOnlyOnlineMapSelected(false);
            setRenderMultipleAgentMap(false);
            setGetAgentMapDetail(0); // Indicates no specific map is selected for single view
        } else if (selectedMapKeys.includes("online")) { // "online" is always exclusive if present due to onChange logic
            selectMaps([], false, true); // Preserve only online map in mapData (for display)
            setOnlineMapSelected(true);
            setOnlyOnlineMapSelected(true);
            setRenderMultipleAgentMap(false); // Online map has its own component or view logic
            setGetAgentMapDetail([]); // No regular map ID needed when online is exclusive
        } else {
            // Only regular maps are selected
            const numericMapIds = selectedMapKeys
                .map(id => parseInt(id)) // No need to filter "online" as it's not present here
                .filter(id => !isNaN(id));
            selectMaps(numericMapIds, true, false); // Preserve selected maps, remove online from mapData
            setOnlineMapSelected(false);
            setOnlyOnlineMapSelected(false);
            setRenderMultipleAgentMap(numericMapIds.length > 1);
            setGetAgentMapDetail(numericMapIds.length === 1 ? numericMapIds[0] : numericMapIds);
        }
    }, [selectedMapKeys, selectMaps, setOnlineMapSelected, setOnlyOnlineMapSelected, setRenderMultipleAgentMap, setGetAgentMapDetail]);


    const checkboxOptions = useMemo(() => {
        const isOnlineStrictlySelected = selectedMapKeys.length === 1 && selectedMapKeys[0] === "online";
        return [
            { label: "Online", value: "online" },
            ...(agentMaps || []).map(map => ({
                label: map.name,
                value: map.agentMapId.toString(),
                disabled: isOnlineStrictlySelected && map.agentMapId.toString() !== "online", // Corrected: map.value to map.agentMapId.toString()
            }))
        ];
    }, [agentMaps, selectedMapKeys]);

    // Modify onChange handler to handle exclusive selection of online map
    const onChange = useCallback((checkedValues: string[]) => {

        let newSelectedMapKeys: string[];

        // If "online" is present in the checked values, it becomes the exclusive selection.
        if (checkedValues.includes("online")) {
            newSelectedMapKeys = ["online"];
        } else {
            // Otherwise, the selection is whatever was checked (can be empty or other maps).
            newSelectedMapKeys = checkedValues;
        }

        setSelectedMapKeys(newSelectedMapKeys);

        // Save selection to localStorage for persistence across page refreshes
        try {
            localStorage.setItem('agentMapSelection', JSON.stringify(newSelectedMapKeys));
            //console.log('💾 Saved agent map selection to localStorage:', newSelectedMapKeys);
        } catch (error) {
            //console.warn('⚠️ Failed to save agent map selection to localStorage:', error);
        }

        if (newSelectedMapKeys.length === 0) {

            selectMaps([], false, false);
            setOnlineMapSelected(false);
            setOnlyOnlineMapSelected(false);
            setRenderMultipleAgentMap(false);
            setGetAgentMapDetail(0);
        }
        else if (newSelectedMapKeys.includes("online")) {

            // Reset the loaded flag so refetch runs again
            setOnlineDataLoaded(false);

            selectMaps([], false, true);
            if (onlineAgentSeatsFromAPI) {
                dispatch(setOnlineAgentMapData(onlineAgentSeatsFromAPI));
            }
            setOnlineMapSelected(true);
            setOnlyOnlineMapSelected(true);
            setRenderMultipleAgentMap(false);
            setGetAgentMapDetail([]);
        }

        // Always refetch on selecting online
        if (newSelectedMapKeys.includes("online")) {
            fetchOnlineSeats().then(resp => {
                if (resp.data?.data) {
                    dispatch(setOnlineAgentMapData(resp.data.data));
                    setOnlineDataLoaded(true);
                }
            });
        }
    }, [
        fetchOnlineSeats,
        dispatch,
        onlineAgentSeatsFromAPI,
        selectMaps,
        setOnlineMapSelected,
        setOnlyOnlineMapSelected,
        setRenderMultipleAgentMap,
        setGetAgentMapDetail
    ]);

    // Render component based on selection state
    const renderAgentMapComponent = () => {
        if (getAgentMapDetail === 0 && !onlyOnlineMapSelected) {
            return (
                <div className="flex items-center justify-center h-full">
                    <Typography.Text className="text-gray-500">Vui lòng chọn một bản đồ</Typography.Text>
                </div>
            );
        }

        if (onlyOnlineMapSelected) {
            return (
                <OnlineAgentMap
                    singleAgentMapAgentStatuses={singleAgentMapAgentStatuses}
                    showCounters={showCounters}
                />
            );
        }

        if (renderMultipleAgentMap) {
            return (
                <MultipleAgentMap
                    singleAgentMapAgentStatuses={singleAgentMapAgentStatuses}
                    showCounters={showCounters}
                />
            );
        }

        return (
            <AccessManageDtvAgentMap
                singleAgentMapAgentStatuses={singleAgentMapAgentStatuses}
                agentMapId={getAgentMapDetail as number}
                showCounters={showCounters}
            />
        );
    };

    return (
        <div>
            <PageHeader className='flex items-center gap-1'>
                <Typography.Title className='text-header-label mb-0'>{sidebarMenuMessages.dashboardAgentMap}</Typography.Title>
            </PageHeader>
            <div className="grid grid-cols-[640px_1fr] bg-gray-100">
                <div className="flex flex-col bg-white rounded-tl-xl shadow-md h-[calc(100vh-100px)]">
                    <Dashboard
                        callStatsBySource={dashboardCallStats} // Pass the state variable
                        getChatSessionDetails={dashboardChatStats} // Pass the state variable
                    // Removed key prop to rely on React.memo and internal updates
                    />
                </div>
                <div className="flex flex-col h-[calc(100vh-100px)]">
                    <div className="flex w-full items-center justify-between rounded-tr-xl bg-[#EBEBED] px-6 py-6">
                        <div className="w-[80%]">
                            <Checkbox.Group
                                options={checkboxOptions}
                                onChange={onChange}
                                value={selectedMapKeys} // Sử dụng giá trị từ state
                            />
                        </div>
                        <div className="w-[20%] flex items-center justify-end gap-2">
                            <Typography.Text className="text-xs">Xem số cuộc gọi & chat</Typography.Text>
                            <Switch
                                checked={showCounters}
                                onChange={setShowCounters}
                            />
                        </div>
                    </div>
                    <div className="bg-[#EBEBED] flex-grow overflow-hidden">
                        {renderAgentMapComponent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccessManageDtv;